import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col, Card, Button, Alert, Spinner, Modal, Form, Badge } from "react-bootstrap";
import axios from "../context/AxiosInstance";
import { AuthContext } from "../context/AuthContext";
import { toast } from "react-toastify";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import AppBreadcrumb from "../components/AppBreadcrumb";
import EmptyState from "../components/common/EmptyState";
import "./PersonnelDashboard.css";

const PersonnelDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [bulletins, setBulletins] = useState([]);
  const [fiches, setFiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [formData, setFormData] = useState({
    mois: "",
    annee: new Date().getFullYear()
  });
  const [passwordData, setPasswordData] = useState({
    ancien_mot_de_passe: "",
    nouveau_mot_de_passe: "",
    confirmer_mot_de_passe: ""
  });
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [codeBulletin, setCodeBulletin] = useState("");

  const moisOptions = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];

  const annees = [2024, 2025, 2026];

  useEffect(() => {
    const touch = async () => {
      try {
        await axios.post("/personnel/touch-derniere-connexion/");
      } catch (e) {
        // silencieux
      }
    };
    touch();
    fetchBulletins();
  }, []);

  const fetchBulletins = async () => {
    try {
      const response = await axios.get("/personnel/mes-bulletins/");
      setBulletins(response.data.data || []);
      setFiches(response.data.fiches_conges || []);
    } catch (err) {
      console.error("Erreur récupération bulletins:", err);
      toast.error("❌ Erreur lors du chargement des bulletins");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBulletin = async () => {
    if (!formData.mois || !formData.annee) {
      toast.error("Veuillez sélectionner un mois et une année");
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post("/personnel/generer-bulletin-personnel/", {
        mois: formData.mois,
        annee: parseInt(formData.annee)
      });

      if (response.data.success) {
        toast.success("✅ Bulletin généré avec succès");
        setShowGenerateModal(false);
        fetchBulletins();
      }
    } catch (err) {
      console.error("Erreur génération bulletin:", err);
      if (err.response?.data?.detail) {
        toast.error(`❌ ${err.response.data.detail}`);
      } else {
        toast.error("❌ Erreur lors de la génération du bulletin");
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadBulletin = async (idBulletin) => {
    try {
      const response = await axios.get(`/personnel/telecharger-bulletin/${idBulletin}`, {
        responseType: 'blob'
      });

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bulletin.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("✅ Bulletin téléchargé avec succès");
    } catch (err) {
      console.error("Erreur téléchargement:", err);
      toast.error("❌ Erreur lors du téléchargement");
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.nouveau_mot_de_passe !== passwordData.confirmer_mot_de_passe) {
      toast.error("❌ Les mots de passe ne correspondent pas");
      return;
    }

    try {
      const response = await axios.post("/personnel/changer-mot-de-passe/", {
        ancien_mot_de_passe: passwordData.ancien_mot_de_passe,
        nouveau_mot_de_passe: passwordData.nouveau_mot_de_passe
      });

      if (response.data.success) {
        toast.success("✅ Mot de passe modifié avec succès");
        setShowChangePasswordModal(false);
        setPasswordData({
          ancien_mot_de_passe: "",
          nouveau_mot_de_passe: "",
          confirmer_mot_de_passe: ""
        });
      }
    } catch (err) {
      console.error("Erreur changement mot de passe:", err);
      if (err.response?.data?.detail) {
        toast.error(`❌ ${err.response.data.detail}`);
      } else {
        toast.error("❌ Erreur lors du changement de mot de passe");
      }
    }
  };

  const handleSetBulletinCode = async () => {
    if (!codeBulletin) {
      toast.error("Veuillez saisir un code de bulletin");
      return;
    }
    try {
      const res = await axios.post("/personnel/definir-code-bulletin/", {
        code_bulletin: codeBulletin,
      });
      if (res.data?.success) {
        toast.success("✅ Code de bulletin mis à jour");
        setShowCodeModal(false);
        setCodeBulletin("");
      }
    } catch (err) {
      console.error("Erreur code bulletin:", err);
      toast.error("❌ Erreur lors de la mise à jour du code");
    }
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100 personnel-dashboard">
        <Navbar />
        <Container fluid className="p-5">
          <AppBreadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Mon Espace" }]} />

          {/* Toolbar */}
          <Card className="mb-3 shadow-sm surface-card toolbar-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="fw-bold mb-1">📊 Mes Bulletins et Fiches de Congés</h5>
                  <p className="text-muted mb-0">Téléchargez vos bulletins et fiches de congés générés par la RH</p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <small className="text-muted d-none d-md-inline">Résultats : {bulletins.length}</small>
                  <Button variant="outline-secondary" onClick={() => fetchBulletins()}>🔄 Actualiser</Button>
                  <Button variant="outline-primary" onClick={() => setShowChangePasswordModal(true)}>🔐 Changer mot de passe</Button>
                  <Button variant="outline-secondary" onClick={() => setShowCodeModal(true)}>🧩 Définir mon code de bulletin</Button>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Liste des bulletins */}
          <Card className="shadow-sm surface-card">
            <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center p-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Chargement des bulletins...</p>
                  </div>
                ) : bulletins.length === 0 ? (
                  <div className="p-4"><EmptyState title="Aucun bulletin disponible" description="Vos bulletins apparaîtront ici lorsqu’ils seront générés par la RH." /></div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0 modern-table table-soft">
                      <thead className="table-light">
                        <tr>
                          <th>Période</th>
                          <th>Date de génération</th>
                          <th>Statut</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulletins.map((bulletin) => (
                          <tr key={bulletin.id_bulletin}>
                            <td>
                              <strong>{bulletin.mois} {bulletin.annee}</strong>
                            </td>
                            <td>{bulletin.date_generation}</td>
                            <td>
                              <Badge bg="success">✅ Disponible</Badge>
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleDownloadBulletin(bulletin.id_bulletin)}
                              >
                                📥 Télécharger
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </Card.Body>
          </Card>
        </Container>

        {/* Fiches de congés */}
        <Container fluid className="p-5 pt-0">
          <Card className="shadow-sm surface-card">
            <Card.Header>🗂️ Mes Fiches de Congés</Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Chargement des fiches...</p>
                </div>
              ) : fiches.length === 0 ? (
                <div className="p-4"><EmptyState title="Aucune fiche" description="Vos fiches de congés apparaîtront ici lorsqu’elles seront générées par la RH." /></div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0 modern-table table-soft">
                    <thead className="table-light">
                      <tr>
                        <th>Année</th>
                        <th>Date de génération</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fiches.map((f) => (
                        <tr key={f.id_fiche}>
                          <td><strong>{f.annee || '—'}</strong></td>
                          <td>{f.date_generation}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={async ()=>{
                                try {
                                  const resp = await axios.get(`/personnel/telecharger-fiche-conge/${f.id_fiche}`, { responseType: 'blob' });
                                  const url = window.URL.createObjectURL(new Blob([resp.data]));
                                  const link = document.createElement('a');
                                  link.href = url; link.setAttribute('download', f.nom_fichier || 'fiche_conge.pdf');
                                  document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
                                } catch(e) {
                                  toast.error('Erreur de téléchargement');
                                }
                              }}
                            >📥 Télécharger</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>

        {/* Modal Code de bulletin */}
      <Modal show={showCodeModal} onHide={() => setShowCodeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>🧩 Définir mon code de bulletin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            Ce code est utilisé lors de la vérification publique d'un bulletin. Gardez-le confidentiel.
          </Alert>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nouveau code</Form.Label>
              <Form.Control
                type="text"
                value={codeBulletin}
                onChange={(e) => setCodeBulletin(e.target.value)}
                placeholder="Saisir un code confidentiel"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCodeModal(false)}>Annuler</Button>
          <Button variant="primary" onClick={handleSetBulletinCode}>Enregistrer</Button>
        </Modal.Footer>
      </Modal>

        {/* Modal Génération Bulletin */}
      <Modal show={showGenerateModal} onHide={() => setShowGenerateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>📊 Générer un Bulletin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>📅 Mois</Form.Label>
                  <Form.Select
                    value={formData.mois}
                    onChange={(e) => setFormData({...formData, mois: e.target.value})}
                  >
                    <option value="">Sélectionner un mois</option>
                    {moisOptions.map((mois) => (
                      <option key={mois} value={mois}>{mois}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>📊 Année</Form.Label>
                  <Form.Select
                    value={formData.annee}
                    onChange={(e) => setFormData({...formData, annee: e.target.value})}
                  >
                    {annees.map((annee) => (
                      <option key={annee} value={annee}>{annee}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGenerateModal(false)}>
            Annuler
          </Button>
          <Button 
            variant="primary" 
            onClick={handleGenerateBulletin}
            disabled={generating || !formData.mois || !formData.annee}
          >
            {generating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Génération...
              </>
            ) : (
              "Générer"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

        {/* Modal Changement Mot de Passe */}
      <Modal show={showChangePasswordModal} onHide={() => setShowChangePasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>🔐 Changer le Mot de Passe</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Ancien mot de passe</Form.Label>
              <Form.Control
                type="password"
                value={passwordData.ancien_mot_de_passe}
                onChange={(e) => setPasswordData({...passwordData, ancien_mot_de_passe: e.target.value})}
                placeholder="Entrez votre ancien mot de passe"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Nouveau mot de passe</Form.Label>
              <Form.Control
                type="password"
                value={passwordData.nouveau_mot_de_passe}
                onChange={(e) => setPasswordData({...passwordData, nouveau_mot_de_passe: e.target.value})}
                placeholder="Entrez votre nouveau mot de passe"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirmer le nouveau mot de passe</Form.Label>
              <Form.Control
                type="password"
                value={passwordData.confirmer_mot_de_passe}
                onChange={(e) => setPasswordData({...passwordData, confirmer_mot_de_passe: e.target.value})}
                placeholder="Confirmez votre nouveau mot de passe"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowChangePasswordModal(false)}>
            Annuler
          </Button>
          <Button 
            variant="primary" 
            onClick={handleChangePassword}
            disabled={!passwordData.ancien_mot_de_passe || !passwordData.nouveau_mot_de_passe || !passwordData.confirmer_mot_de_passe}
          >
            Changer
          </Button>
        </Modal.Footer>
      </Modal>
      </div>
    </div>
  );
};

export default PersonnelDashboard;
